import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { useNavigate } from 'react-router'
import type { ABSENCE_LOCK_CONFIGS_QUERY } from '../queries'
import { getAbsenceLockActionState } from '../util'
import { useMutation } from '@apollo/client'
import { DELETE_ABSENCE_LOCK_CONFIG_MUTATION } from '../mutations'

type AbsenceLockConfig = ResultOf<
  typeof ABSENCE_LOCK_CONFIGS_QUERY
>['absenceLockConfigs'][number]

interface Props {
  configs: AbsenceLockConfig[]
}

export function AbsenceLockConfigsList({ configs }: Props) {
  const translations = useTranslate({
    name: 'absences.absence-dialog.configs.name',
    actions: 'absences.absence-dialog.configs.actions',
    admin: 'absences.absence-dialog.configs.admin-override',
    permanent: [
      'absences.absence-dialog.configs.permanent',
      { abbreviate: true },
    ],

    notFound: 'absences.absence-dialog.configs.no-configs-found',
  })

  const navigate = useNavigate()

  const [deleteLockConfig] = useMutation(DELETE_ABSENCE_LOCK_CONFIG_MUTATION, {
    refetchQueries: ['AbsenceLockConfigs'],
    awaitRefetchQueries: true,
  })

  return (
    <div className="grid grid-cols-[1fr_repeat(5,auto)] gap-x-2">
      <div
        className={cn(
          'grid col-span-full grid-cols-subgrid p-2',
          'bg-white border-t border-x border-border rounded-t-md'
        )}
      >
        <div className="text-xs font-semibold text-stone-600">
          {translations.name}
        </div>

        <div className="text-xs font-semibold text-stone-600">
          {translations.actions}
        </div>

        <div className="text-xs font-semibold text-stone-600">
          {translations.admin}
        </div>

        <div className="text-xs font-semibold text-stone-600">
          {translations.permanent({ abbreviate: true })}
        </div>
      </div>

      {configs.map(cfg => {
        const lock = getAbsenceLockActionState(cfg.lockActions)

        return (
          <div
            key={cfg.id}
            className={cn(
              'grid col-span-full grid-cols-subgrid p-2',
              'bg-white text-sm border-t border-x border-border last:border-b last:rounded-b-md'
            )}
          >
            <div>{cfg.name}</div>

            <div className="text-center">
              <span
                className={cn(
                  lock.createLocked ? 'text-red-500' : 'text-green-500'
                )}
              >
                C
              </span>
              /
              <span
                className={cn(
                  lock.updateLocked ? 'text-red-500' : 'text-green-500'
                )}
              >
                U
              </span>
              /
              <span
                className={cn(
                  lock.deleteLocked ? 'text-red-500' : 'text-green-500'
                )}
              >
                D
              </span>
            </div>

            <div className="text-center">
              <FAIcon icon={cfg.adminOverride ? 'check' : 'times'} />
            </div>

            <div className="text-center">
              <FAIcon icon={cfg.permanent ? 'check' : 'times'} />
            </div>

            <div>
              <FAIcon
                className="cursor-pointer hover:opacity-70"
                role="button"
                icon="edit"
                onClick={() => navigate(`/absences/edit-lock/${cfg.id}`)}
              />
            </div>

            <div>
              <FAIcon
                className="text-red-500 cursor-pointer hover:opacity-70"
                role="button"
                icon="trash"
                onClick={() => deleteLockConfig({ variables: { id: cfg.id } })}
              />
            </div>
          </div>
        )
      })}

      {!configs.length && (
        <div
          className={cn(
            'grid col-span-full grid-cols-subgrid p-2',
            'bg-white text-sm border-t border-x border-border last:border-b last:rounded-b-md'
          )}
        >
          <div className="col-span-full">{translations.notFound}</div>
        </div>
      )}
    </div>
  )
}
